import { P2Role, type RecordEntityId, type SiteLocation } from '@centric-os/types';
import { defineStore, type StateTree } from 'pinia';
import { useFileConverter } from '@centric-os/helpers';
import { cloneDeep, get } from 'lodash-es';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import type {
  PutCentricosBusinessUnitDropOffBody,
  ValidateVertexResponse,
} from '@compassdigital/sdk.typescript/interface/centricos';
import type { Config } from '@compassdigital/sdk.typescript/interface/config';

/**
 * @interface State
 * @extends {StateTree}
 * @description Defines the state structure for the business unit store.
 */
interface State extends StateTree {
  /**
   * @property {SiteLocation | null} businessUnit - The current business unit data.
   */
  businessUnit: SiteLocation | null;
  /**
   * @property {Config | null} dropOffPublicConfig - Public config holding the shared drop-off display toggles.
   */
  dropOffPublicConfig: Config | null;
  /**
   * @property {Config | null} dropOffPrivateConfig - Private config holding the shared drop-off delivery fee.
   */
  dropOffPrivateConfig: Config | null;
}

/**
 * @function useBusinessUnitStore
 * @param {string} [storeId='siteLocation'] - The ID for the Pinia store.
 * @returns {ReturnType<typeof defineStore>} A configured Pinia store instance.
 * @description Creates and returns a Pinia store for managing business unit data.
 */
export function useBusinessUnitStore(storeId = 'siteLocation') {
  const store = defineStore(storeId, {
    state: (): State => ({
      businessUnit: null,
      dropOffPublicConfig: null,
      dropOffPrivateConfig: null,
    }),
    actions: {
      /**
       * @async
       * @function createBusinessUnit
       * @param {Partial<SiteLocation>} payload - Partial data for creating a new business unit.
       * @param {RecordEntityId} siteId - The ID of the site to associate with the new business unit.
       * @returns {Promise<void>}
       * @description Creates a new business unit and associates it with the specified site.
       * @throws {Error} If the API requests fail.
       */
      async createBusinessUnit(
        payload: Partial<SiteLocation>,
        siteId: RecordEntityId,
      ): Promise<void> {
        await this.serviceApi
          // @ts-expect-error FIXME: hand-maintained SiteLocation vs SDK Location body (TS2345)
          .post_location({ ...payload })
          .then((response) =>
            this.serviceApi.patch_location_group(String(siteId), {
              locations: [{ id: response.id }],
            }),
          );
      },

      /**
       * @async
       * @function getBusinessUnit
       * @param {RecordEntityId} id - The ID of the business unit to retrieve.
       * @returns {Promise<void>}
       * @description Fetches a business unit by its ID and updates the store state.
       * @throws {Error} If the API request fails.
       */
      async getBusinessUnit(id: RecordEntityId): Promise<void> {
        const response = await this.serviceApi.get_location(String(id), {
          query: {
            nocache: true,
            show_disabled_marketplace: true,
          },
        });
        // @ts-expect-error FIXME: SDK Location vs hand-maintained SiteLocation (TS2322)
        this.businessUnit = response;
      },

      /**
       * @async
       * @function updateBusinessUnit
       * @param {Partial<SiteLocation>} formData - Partial data for updating the business unit.
       * @param {RecordEntityId} id - The ID of the business unit to update.
       * @returns {Promise<void>}
       * @description Updates a business unit with the given payload and updates the store state.
       * @throws {Error} If the API request fails.
       */
      async updateBusinessUnit(formData: Partial<SiteLocation>, id: RecordEntityId): Promise<void> {
        const payload: Partial<SiteLocation> = cloneDeep(formData);
        // Written by the Edge, not this patch.
        delete payload.drop_off;
        if (payload?.market_place?.logo && payload.market_place.logo instanceof File) {
          const url = await this.uploadFile(payload.market_place.logo);
          payload.market_place.logo = url;
        }
        payload.market_place.is.pickup_supported = payload.market_place.is.enabled;
        // @ts-expect-error FIXME: hand-maintained SiteLocation vs SDK Location body (TS2345)
        const response = await this.serviceApi.patch_location(String(id), payload);
        // @ts-expect-error FIXME: SDK Location vs hand-maintained SiteLocation (TS2322)
        this.businessUnit = response;
      },

      /**
       * @async
       * @function getBusinessUnitDropOff
       * @param {RecordEntityId} id - The ID of the business unit.
       * @returns {Promise<void>}
       * @description Loads the configs holding the business unit's shared drop-off settings. A
       * business unit that has never saved them has no config records yet, so a 404 is a valid
       * empty result rather than a failure.
       */
      async getBusinessUnitDropOff(id: RecordEntityId): Promise<void> {
        const [publicConfig, privateConfig] = await Promise.all([
          this.serviceApi.get_config_public(String(id)).ignore(404),
          this.serviceApi.get_config(String(id)).ignore(404),
        ]);
        this.dropOffPublicConfig = publicConfig ?? {};
        this.dropOffPrivateConfig = privateConfig ?? {};
      },

      /**
       * @async
       * @function updateBusinessUnitDropOff
       * @param {RecordEntityId} id - The ID of the business unit.
       * @param {PutCentricosBusinessUnitDropOffBody} payload - The shared drop-off settings to save.
       * @returns {Promise<void>}
       * @description Saves the shared drop-off settings through the Edge, which writes the
       * business unit and then applies them to each of its drop-off stations.
       * @throws {Error} If the settings could not be saved or could not be applied to a station.
       */
      async updateBusinessUnitDropOff(
        id: RecordEntityId,
        payload: PutCentricosBusinessUnitDropOffBody,
      ): Promise<void> {
        await this.serviceApi.put_centricos_business_unit_drop_off(String(id), payload);
      },

      /**
       * @async
       * @function uploadFile
       * @param {File} file - The file to be uploaded.
       * @returns {Promise<string>} The URL of the uploaded file.
       * @description Uploads a file and returns its URL.
       * @throws {Error} If the file upload fails.
       */
      async uploadFile(file: File): Promise<string> {
        try {
          const { getBase64 } = useFileConverter();
          const logoBase64: string = (await getBase64(file)) as string;
          const response = await this.serviceApi.post_file({
            file_base64: logoBase64.replace('data:image/png;base64,', ''),
            file_name: file.name.replace(/\s/g, '_'),
          });
          return response.url;
        } catch (error) {
          console.error('Error uploading logo', error);
          throw error;
        }
      },
      async enableVertex(id: RecordEntityId): Promise<ValidateVertexResponse> {
        return this.serviceApi.post_centricos_business_unit_validate_vertex(String(id));
      },

      async deleteBusinessUnit(businessUnitId: RecordEntityId): Promise<void> {
        await this.serviceApi.delete_centricos_business_unit(String(businessUnitId));
      },
    },
    getters: {
      currentBusinessUnit: (state): Partial<SiteLocation> => state.businessUnit,
      isMarketPlaceEnabled: (state: State): boolean => {
        return !!get(
          state.businessUnit,
          'market_place.is.enabled',
          get(state.businessUnit, 'market_place.label.en', false),
        );
      },
      canModifyMarketPlace: (_state: State): boolean => {
        const authStore = useAuthStore();
        return authStore.hasRole(P2Role.ADMIN) || authStore.hasRole(P2Role.SITE_OPERATOR);
      },
    },
  });
  return store();
}
